type TRequestMethod = "GET" | "POST" | "PUT" | "DELETE";
export type TRequestQuery = Record<string, string>;
type TRequestHeaders = Record<string, string>;

type TRequestOptions<TREQ> = {
  method: TRequestMethod;
  url: string;
  query?: TRequestQuery;
  headers?: TRequestHeaders;
  body?: TREQ;
};

type TResponse<TRES> = {
  ok: boolean;
  status: number;
  body: TRES | null;
};

type TToken = {
  access_token: string | null;
  //  refresh_token: string | null;  TODO
};

export default class HomaticApiClient {
  baseUrl = "/api";

  async request<TREQ, TRES>(
    options: TRequestOptions<TREQ>,
  ): Promise<TResponse<TRES>> {
    let response = await this.requestInternal<TREQ, TRES>(options);
    if (response.status === 401 && options.url !== "/tokens") {
      const refreshResponse = await this.put<TToken, TToken>("/tokens", {
        access_token: localStorage.getItem("accessToken"),
      });
      if (refreshResponse.ok && refreshResponse.body) {
        const payload = refreshResponse.body as TToken;
        localStorage.setItem("accessToken", payload.access_token || "");
        response = await this.requestInternal<TREQ, TRES>(options);
      }
    }
    return response;
  }

  async requestInternal<TREQ, TRES>(
    options: TRequestOptions<TREQ>,
  ): Promise<TResponse<TRES>> {
    let query = new URLSearchParams(options.query || {}).toString();
    if (query !== "") {
      query = "?" + query;
    }

    let url;
    let response;
    try {
      url = this.baseUrl + options.url + query;
      response = await fetch(url, {
        method: options.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
          ...options.headers,
        },
        credentials: options.url === "/tokens" ? "include" : "omit",
        body: options.body ? JSON.stringify(options.body) : null,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      response = {
        ok: false,
        status: 500,
        json: async () => {
          return {
            code: 500,
            message: "The server is unresponsive",
            description: error.toString(),
          };
        },
      };
    }

    const payload = response.status !== 204 ? await response.json() : null;
    /*
    console.log(
      "[HomaticApiClient.request] url=" +
        url +
        " [" +
        options.method +
        "] ok=" +
        response.ok +
        ", status=" +
        response.status,
    );
    */
    return {
      ok: response.ok,
      status: response.status,
      body: payload,
    };
  }

  async get<TRES>(
    url: string,
    query?: TRequestQuery,
    options?: TRequestOptions<null>,
  ): Promise<TResponse<TRES>> {
    return this.request<null, TRES>({ method: "GET", url, query, ...options });
  }

  async post<TREQ, TRES>(
    url: string,
    body?: TREQ,
    options?: TRequestOptions<TREQ>,
  ): Promise<TResponse<TRES>> {
    return this.request<TREQ, TRES>({ method: "POST", url, body, ...options });
  }

  async put<TREQ, TRES>(
    url: string,
    body?: TREQ,
    options?: TRequestOptions<TREQ>,
  ): Promise<TResponse<TRES>> {
    return this.request<TREQ, TRES>({ method: "PUT", url, body, ...options });
  }

  async delete(url: string, options?: TRequestOptions<null>) {
    return this.request<null, null>({ method: "DELETE", url, ...options });
  }

  async login(
    username: string,
    password: string,
  ): Promise<"ok" | "fail" | "error"> {
    const response = await this.request<null, TToken>({
      method: "POST",
      url: "/tokens",
      headers: {
        Authorization: "Basic " + window.btoa(username + ":" + password),
      },
    });
    if (!response.ok || !response.body) {
      return response.status === 401 ? "fail" : "error";
    }
    const payload = response.body as TToken;
    localStorage.setItem("accessToken", payload.access_token || "");
    return "ok";
  }

  async logout() {
    await this.delete("/tokens");
    localStorage.removeItem("accessToken");
  }

  isAuthenticated() {
    return localStorage.getItem("accessToken") !== null;
  }
}
